import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, 
  Clock, 
  Target, 
  BookOpen, 
  TrendingUp, 
  Star, 
  ArrowRight,
  CheckCircle,
  Calendar,
  Zap,
  Brain,
  Award
} from 'lucide-react';

interface Recommendation {
  id: string;
  type: 'lesson' | 'course' | 'practice' | 'review' | 'goal';
  title: string;
  description: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  estimatedTime: number; // in minutes
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  category: string;
  progress?: number; // 0-100
  actionUrl: string;
  actionText: string;
  icon: React.ElementType;
  color: string;
}

interface SmartRecommendationsProps {
  userId?: string;
  userProfile?: any;
  onRecommendationClick?: (recommendation: Recommendation) => void;
}

const SmartRecommendations: React.FC<SmartRecommendationsProps> = ({ 
  userId, 
  userProfile,
  onRecommendationClick 
}) => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock recommendations based on user profile and learning patterns
      const mockRecommendations: Recommendation[] = [
        {
          id: '1',
          type: 'lesson',
          title: 'Understanding Credit Scores',
          description: 'Learn how credit scores work and how to improve yours',
          reason: 'Based on your interest in financial literacy and current knowledge level',
          priority: 'high',
          estimatedTime: 15,
          difficulty: 'beginner',
          category: 'Financial Literacy',
          progress: 0,
          actionUrl: '/quick-course/credit-basics',
          actionText: 'Start Lesson',
          icon: BookOpen,
          color: 'blue'
        },
        {
          id: '2',
          type: 'practice',
          title: 'Practice Banking Vocabulary',
          description: 'Review and practice essential banking terms',
          reason: 'You\'ve completed banking basics - time to reinforce your knowledge',
          priority: 'medium',
          estimatedTime: 10,
          difficulty: 'beginner',
          category: 'Banking',
          progress: 0,
          actionUrl: '/quick-course/banking-system',
          actionText: 'Start Practice',
          icon: Brain,
          color: 'green'
        },
        {
          id: '3',
          type: 'course',
          title: 'Health Insurance Basics',
          description: 'Learn the words on a U.S. insurance card and where to go for care',
          reason: 'A free path you can finish, with four short lessons',
          priority: 'high',
          estimatedTime: 45,
          difficulty: 'beginner',
          category: 'Healthcare',
          progress: 0,
          actionUrl: '/courses/health-insurance',
          actionText: 'Start Course',
          icon: Target,
          color: 'purple'
        },
        {
          id: '4',
          type: 'review',
          title: 'Review: Building Credit Basics',
          description: 'Refresh your knowledge of credit building fundamentals',
          reason: 'You completed this lesson 3 days ago - time for a quick review',
          priority: 'medium',
          estimatedTime: 8,
          difficulty: 'beginner',
          category: 'Financial Literacy',
          progress: 100,
          actionUrl: '/quick-course/credit-basics',
          actionText: 'Review Again',
          icon: CheckCircle,
          color: 'orange'
        },
        {
          id: '5',
          type: 'goal',
          title: 'Set Learning Goals for This Week',
          description: 'Plan your learning objectives and track your progress',
          reason: 'You haven\'t set goals this week - planning helps with motivation',
          priority: 'low',
          estimatedTime: 5,
          difficulty: 'beginner',
          category: 'Planning',
          progress: 0,
          actionUrl: '/milestones',
          actionText: 'Set Goals',
          icon: Calendar,
          color: 'indigo'
        }
      ];

      setRecommendations(mockRecommendations);
      setLoading(false);
    };

    fetchRecommendations();
  }, [userId, userProfile]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'medium':
        return 'border-yellow-200 bg-yellow-50';
      case 'low':
        return 'border-green-200 bg-green-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Zap className="h-4 w-4 text-red-500" />;
      case 'medium':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Star className="h-4 w-4 text-green-500" />;
      default:
        return <Lightbulb className="h-4 w-4 text-gray-500" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-green-600 bg-green-100';
      case 'intermediate':
        return 'text-yellow-600 bg-yellow-100';
      case 'advanced':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 animate-pulse">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Lightbulb className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Smart Recommendations</h2>
            <p className="text-sm text-gray-600">Personalized suggestions for your learning journey</p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {recommendations.length} suggestions
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {recommendations.map((recommendation) => (
          <div
            key={recommendation.id}
            className={`bg-white rounded-xl p-6 shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer ${getPriorityColor(recommendation.priority)}`}
            onClick={() => onRecommendationClick?.(recommendation)}
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`w-12 h-12 bg-${recommendation.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                <recommendation.icon className={`h-6 w-6 text-${recommendation.color}-600`} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {recommendation.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {recommendation.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {getPriorityIcon(recommendation.priority)}
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getDifficultyColor(recommendation.difficulty)}`}>
                      {recommendation.difficulty}
                    </span>
                  </div>
                </div>

                {/* Reason */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div className="flex items-start gap-2">
                    <Brain className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Why this matters:</span> {recommendation.reason}
                    </p>
                  </div>
                </div>

                {/* Progress Bar (if applicable) */}
                {recommendation.progress !== undefined && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Progress</span>
                      <span className="text-xs text-gray-600">{recommendation.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-${recommendation.color}-500 h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${recommendation.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatTime(recommendation.estimatedTime)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      <span>{recommendation.category}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // In a real app, you'd navigate to the action URL
                      console.log('Navigate to:', recommendation.actionUrl);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 bg-${recommendation.color}-600 text-white rounded-lg hover:bg-${recommendation.color}-700 transition-colors text-sm font-medium`}
                  >
                    {recommendation.actionText}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {recommendations.length === 0 && (
        <div className="text-center py-12">
          <Lightbulb className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations yet</h3>
          <p className="text-gray-600 mb-4">
            Complete a few lessons to get personalized recommendations
          </p>
          <button className="btn btn-primary">
            Start Learning
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartRecommendations;
